import React, { useState } from 'react';

import { CoachingInstanceGQL } from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { refundCharge } from 'coaching/actions/CoachingActions';
import ToastActions from 'actions/ToastActions';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import { userReadableDateFormat, formatTimeForCoordinator } from 'lib/dateTimeFormat';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import MediaObject from 'components/MediaObject';
import { RefundReasonsSelect } from 'coaching/components/CoachingCoordinatorView/RefundReasonsSelect';
import Row from 'components/Row';
import SidebarHeader from 'coaching/components/SidebarHeader';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';

type Props = {
  instance: CoachingInstanceGQL;
  userName?: string;
  duration?: number;
  participantUuid?: string;
};

export default function CoachingCoordinatorTransactionSidebar({
  instance,
  userName,
  duration,
  participantUuid,
}: Props) {
  // TODO: we can handle the back button properly once the stacking sidebar PR is merged
  const { closeSidebar } = useCoachingViewState();
  const instanceStartDate = userReadableDateFormat(instance.startDateTime);
  const instanceStartTime = formatTimeForCoordinator(instance.startDateTime);
  const [refundReason, setRefundReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasInsufficientPermissions, setHasInsufficientPermissions] = useState(false);
  const canSubmitRefund = Boolean(participantUuid && refundReason);

  async function handleRefundSubmit() {
    if (canSubmitRefund) {
      setSubmitting(true);
      await refundCharge(
        {
          instanceUuid: instance.eventId,
          participantUuid: participantUuid!,
          // TODO: use real chargeUuid here once we have it from the fetched transactions
          chargeUuid: '',
          refundReason,
        },
        () => setHasInsufficientPermissions(true)
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to refund a session without selecting a refund reason. Please try again.'
      );
    }
  }

  return (
    <>
      <SidebarHeader
        data-test="transactions-header"
        title="View transactions"
        onClose={() => closeSidebar()}
      />

      <BoxBody size="xsmall">
        <Row size="small">
          <Heading level="2">{userName}</Heading>
          <Text element="div">{instanceStartDate}</Text>
          <Text element="div">
            {`${instanceStartTime}`}
            {duration && ` • ${duration} min`}
          </Text>
        </Row>
        <Divider />
        <Row size="small">
          <Heading level="4">Transactions</Heading>
        </Row>
        <>{/* Transactions list will be rendered here, weeee */}</>
        {/* TODO: add logic for showing retry charge vs. refund button based on transactions */}
        {hasInsufficientPermissions ? (
          <BoxBody size="xsmall" rounded dark>
            <TextWithIcon icon="explanation-circle">
              You don’t have permission. Reach out to a CCM to process a refund.
            </TextWithIcon>
          </BoxBody>
        ) : (
          <>
            <Row size="small">
              <RefundReasonsSelect changeState={setRefundReason} />
            </Row>
            <Row size="small">
              <Button
                full
                color="apple"
                disabled={!canSubmitRefund}
                onClick={handleRefundSubmit}
                loading={submitting}>
                {/* TODO: put real refund amount here */}
                Refund $0.00
              </Button>
            </Row>
          </>
        )}
        <Divider />
        <Link href={LinkConstants.STRIPE_TRANSACTIONS_DASHBOARD} target="_blank">
          <MediaObject gutter="xxsmall">
            <Text weight="semibold">View transactions in Stripe</Text>
            <Text align="inherit">
              <Icon name="external-link"></Icon>
            </Text>
          </MediaObject>
        </Link>
      </BoxBody>
    </>
  );
}
